import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../lib/utils";

interface WebViewTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  className?: string;
  showOnlyWhenTruncated?: boolean; // NEW PROP
}

export const WebViewTooltip: React.FC<WebViewTooltipProps> = ({
  children,
  content,
  side = "bottom",
  sideOffset = 8,
  className,
  showOnlyWhenTruncated = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isTruncated, setIsTruncated] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tooltipId = useRef(
    `tooltip-${Math.random().toString(36).substr(2, 9)}`
  ).current;

  const updatePosition = () => {
    if (!triggerRef.current || !contentRef.current) return;

    // Safety check to prevent DOM manipulation errors
    if (
      !document.contains(triggerRef.current) ||
      !document.contains(contentRef.current)
    ) {
      return;
    }

    const triggerElement = triggerRef.current.firstElementChild as HTMLElement;
    if (!triggerElement) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 12; // Minimum padding from viewport edges

    let top = 0;
    let left = 0;

    // Calculate preferred position based on requested side
    switch (side) {
      case "top":
        top = triggerRect.top - contentRect.height - sideOffset;
        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + sideOffset;
        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
        left = triggerRect.left - contentRect.width - sideOffset;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
        left = triggerRect.right + sideOffset;
        break;
    }

    // Smart viewport-aware positioning for tooltips
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    // Check if we need to flip sides based on available space
    if (
      side === "top" &&
      spaceAbove < contentRect.height + sideOffset &&
      spaceBelow > spaceAbove
    ) {
      // Flip to bottom if there's more space below
      top = triggerRect.bottom + sideOffset;
    } else if (
      side === "bottom" &&
      spaceBelow < contentRect.height + sideOffset &&
      spaceAbove > spaceBelow
    ) {
      // Flip to top if there's more space above
      top = triggerRect.top - contentRect.height - sideOffset;
    } else if (
      side === "left" &&
      spaceLeft < contentRect.width + sideOffset &&
      spaceRight > spaceLeft
    ) {
      // Flip to right if there's more space on the right
      left = triggerRect.right + sideOffset;
    } else if (
      side === "right" &&
      spaceRight < contentRect.width + sideOffset &&
      spaceLeft > spaceRight
    ) {
      // Flip to left if there's more space on the left
      left = triggerRect.left - contentRect.width - sideOffset;
    }

    // Adjust horizontal position to stay within viewport
    if (left < padding) {
      left = padding;
    } else if (left + contentRect.width > viewportWidth - padding) {
      left = viewportWidth - contentRect.width - padding;
    }

    // Adjust vertical position to stay within viewport
    if (top < padding) {
      top = padding;
    } else if (top + contentRect.height > viewportHeight - padding) {
      top = viewportHeight - contentRect.height - padding;
    }

    // For narrow viewports, ensure tooltip doesn't go off-screen
    if (viewportWidth < 480) {
      left = Math.max(
        padding,
        Math.min(left, viewportWidth - contentRect.width - padding)
      );
    }

    setPosition({ top, left });
  };

  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure DOM is ready for measurement
      requestAnimationFrame(() => {
        updatePosition();
      });
      const handleResize = () => updatePosition();
      window.addEventListener("resize", handleResize);
      return () => {
        try {
          window.removeEventListener("resize", handleResize);
        } catch {
          // Ignore cleanup errors
        }
      };
    }
  }, [isOpen, side, sideOffset, content]);

  // Truncation detection for conditional tooltip display
  useEffect(() => {
    if (!showOnlyWhenTruncated || !triggerRef.current) return;

    const checkTruncation = () => {
      // Find the span element that contains the text (not the icons)
      // For badges, look for the inner span with text content
      // For customer names, look for the span with text content (may be nested in div with role="presentation")
      const element =
        (triggerRef.current?.querySelector("span span") as HTMLElement) ||
        (triggerRef.current?.querySelector(
          "div[role='presentation'] span"
        ) as HTMLElement) ||
        (triggerRef.current?.querySelector("span") as HTMLElement);
      if (element) {
        // Check if the element has text content and is actually truncated
        const hasText = Boolean(
          element.textContent && element.textContent.trim().length > 0
        );
        const truncated = hasText && element.scrollWidth > element.clientWidth;
        setIsTruncated(truncated);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(checkTruncation);
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [showOnlyWhenTruncated]);

  return (
    <>
      <div
        ref={triggerRef}
        style={{ display: "contents" }}
        role="presentation"
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        {children}
      </div>

      {isOpen &&
        (!showOnlyWhenTruncated || isTruncated) &&
        createPortal(
          <div
            id={tooltipId}
            ref={contentRef}
            role="tooltip"
            className={cn(
              "webview-tooltip",
              "fixed z-[99999] overflow-hidden rounded-md px-3 py-1.5 text-xs shadow-md",
              "animate-in fade-in-0 zoom-in-95",
              className
            )}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};
